import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Radio, Tv, ArrowRight, Newspaper, Crown, Trophy, Flame, Video, Zap, CreditCard, Ticket, Vote, Mic2, ShieldCheck, Users, Phone } from "lucide-react";
import { endpoints } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { useAuth } from "@/context/AuthContext";
import { AdSlot } from "@/components/AdSlot";
import { HomepageVideo } from "@/components/HomepageVideo";

const TILES = [
  { id: "live-radio", to: "/radio", icon: Radio, label: "Live Radio", hint: "89.7 FM, 24/7", color: "#E11D2A" },
  { id: "live-tv", to: "/live", icon: Tv, label: "Live TV", hint: "Watch our channels", color: "#2563EB" },
  { id: "highlights", to: "/highlights", icon: Flame, label: "Highlights", hint: "Goals & big moments", color: "#F59E0B" },
  { id: "football", to: "/football", icon: Trophy, label: "Football", hint: "Scores, RPL & scorers", color: "#10B981" },
  { id: "shows", to: "/vod", icon: Video, label: "Shows", hint: "Watch on demand", color: "#A78BFA" },
  { id: "utilities", to: "/utilities", icon: Zap, label: "Utilities", hint: "Airtime & power + 48h free", color: "#FACC15" },
  { id: "pay", to: "/pay", icon: CreditCard, label: "Pay", hint: "Pay merchants by MoMo", color: "#14B8A6" },
  { id: "events", to: "/events", icon: Ticket, label: "Events", hint: "Buy your tickets", color: "#EC4899" },
  { id: "vote", to: "/vote", icon: Vote, label: "Vote", hint: "Support your favourite", color: "#6366F1" },
  { id: "news", to: "/news", icon: Newspaper, label: "News", hint: "Latest stories", color: "#94A3B8" },
  { id: "creators", to: "/creators", icon: Mic2, label: "Creators", hint: "Hosts & creators", color: "#22D3EE" },
  { id: "pricing", to: "/pricing", icon: ShieldCheck, label: "Plans", hint: "Daily to yearly", color: "#E11D2A" },
];

const ServiceTile = ({ t, i }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.03 }}>
    <Link to={t.to} data-testid={`service-tile-${t.id}`}
      className="group h-full min-h-[104px] sm:min-h-[120px] rounded-2xl bg-[#121620] border border-[#273042] p-3 sm:p-4 flex flex-col justify-between hover:border-[#3a4560] hover:bg-[#161b27] transition-colors">
      <span className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
        style={{ background: `${t.color}22`, color: t.color }}><t.icon className="w-5 h-5" /></span>
      <span className="mt-3 block">
        <span className="block text-white font-bold text-sm sm:text-base leading-tight">{t.label}</span>
        <span className="block text-[11px] sm:text-xs text-slate-400 mt-0.5 leading-snug">{t.hint}</span>
      </span>
    </Link>
  </motion.div>
);

const LiveStrip = ({ live, nowPlaying }) => {
  const first = (live.streams || []).find((s) => s.isLive);
  const more = (live.streams || []).filter((s) => s.isLive).length - 1;
  return (
    <Link to={first ? "/live" : "/radio"} data-testid="live-now-strip"
      className="flex items-center gap-3 rounded-2xl border border-[#E11D2A]/40 bg-gradient-to-r from-[#E11D2A]/25 to-[#121620] px-4 py-3 hover:border-[#E11D2A] transition-colors">
      <span className="flex items-center gap-1.5 shrink-0 px-2 py-0.5 rounded-md bg-[#E11D2A] text-white text-[10px] font-extrabold uppercase tracking-wider">
        <span className="w-1.5 h-1.5 rounded-full bg-white live-dot" /> Live
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-white text-sm font-semibold truncate" data-testid="live-now-title">
          {first ? first.title : nowPlaying?.showTitle || "B&B KIGALI FM 89.7"}{more > 0 && <span className="text-slate-300 font-medium"> · +{more} more</span>}
        </span>
        <span className="block text-xs text-slate-400 truncate">{first ? "Now on Live TV" : `On air now${nowPlaying?.djName ? ` · ${nowPlaying.djName}` : ""}`}</span>
      </span>
      <span className="inline-flex items-center gap-1 text-xs font-bold text-white shrink-0">{first ? "Watch" : "Listen"} <ArrowRight className="w-3.5 h-3.5" /></span>
    </Link>
  );
};

const PlansStrip = ({ plans, isSubscribed }) => {
  const daily = plans.filter((p) => p.period === "daily").sort((a, b) => a.amount - b.amount)[0];
  return (
    <Link to="/pricing" data-testid="home-plans-strip"
      className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-[#2563EB]/40 bg-gradient-to-br from-[#0B1220] to-[#0A0C10] p-4 sm:p-5 hover:border-[#2563EB] transition-colors">
      <Crown className="w-8 h-8 text-[#2563EB] shrink-0" />
      <span className="flex-1 min-w-0">
        <span className="block text-white font-bold">{isSubscribed ? "You're a member — renew or upgrade any time" : `Unlock radio, live TV & shows${daily ? ` from ${Number(daily.amount).toLocaleString()} RWF/day` : ""}`}</span>
        <span className="block text-xs text-slate-400 mt-0.5">Daily, weekly, monthly or yearly · MTN Mobile Money, card or PayPal</span>
      </span>
      <span className="inline-flex items-center justify-center gap-1.5 h-10 px-5 rounded-full bg-[#2563EB] text-white text-sm font-bold shrink-0">See all plans <ArrowRight className="w-4 h-4" /></span>
    </Link>
  );
};

const NewsList = ({ news }) => (
  <section className="mt-8" data-testid="home-news">
    <div className="flex items-end justify-between mb-3">
      <h2 className="text-lg sm:text-xl font-bold text-white">Latest news</h2>
      <Link to="/news" data-testid="home-news-all" className="text-sm text-[#E11D2A] font-semibold hover:underline flex items-center gap-1">All news <ArrowRight className="w-4 h-4" /></Link>
    </div>
    <div className="rounded-2xl bg-[#121620] border border-[#273042] divide-y divide-[#273042]">
      {news.map((n) => (
        <Link key={n.id} to="/news" data-testid={`home-news-${n.id}`} className="flex items-center gap-3 p-3 hover:bg-white/5 transition-colors">
          <img src={n.coverUrl || n.thumbnail || "/bb-logo.png"} alt="" loading="lazy" className="w-16 h-12 rounded-lg object-cover shrink-0" onError={(e) => { e.currentTarget.src = "/bb-logo.png"; }} />
          <span className="min-w-0">
            <span className="block text-[10px] uppercase tracking-wider text-[#E11D2A] font-bold">{n.category || "News"}</span>
            <span className="block text-sm text-white font-semibold line-clamp-2">{n.title}</span>
          </span>
        </Link>
      ))}
    </div>
  </section>
);

const TeamStrip = ({ team }) => (
  <section className="mt-6 rounded-2xl border border-[#273042] bg-[#121620] p-4 sm:p-5" data-testid="home-team">
    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex -space-x-3 shrink-0">
        {team.slice(0, 5).map((j) => (
          <img key={j.id} src={j.photo || "/bb-logo.png"} alt={j.name} title={j.name} loading="lazy" onError={(e) => { e.currentTarget.src = "/bb-logo.png"; }}
            className="w-11 h-11 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-[#121620] bg-[#0A0C10]" />
        ))}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-bold">Meet our journalists</div>
        <div className="text-xs text-slate-400">The voices behind B&B KIGALI FM 89.7 — see who we are.</div>
      </div>
      <div className="flex gap-2 shrink-0">
        <Link to="/about" data-testid="home-who-we-are-btn" className="h-10 px-4 rounded-full bg-[#E11D2A] hover:bg-[#C8121F] text-white text-sm font-bold inline-flex items-center gap-1.5 transition-colors"><Users className="w-4 h-4" /> Who we are</Link>
        <Link to="/contact" data-testid="home-contact-btn" className="h-10 px-4 rounded-full bg-white/5 border border-[#273042] text-white text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-white/10 transition-colors"><Phone className="w-4 h-4" /> Contact</Link>
      </div>
    </div>
  </section>
);

export default function Home() {
  const { nowPlaying, requestPlay, isPlaying } = usePlayer();
  const { isAuthed, isSubscribed } = useAuth();
  const [news, setNews] = useState([]);
  const [live, setLive] = useState({ anyLive: false, streams: [] });
  const [plans, setPlans] = useState([]);
  const [cfg, setCfg] = useState({});
  const [team, setTeam] = useState([]);
  const h = cfg.home || {};

  useEffect(() => {
    endpoints.news().then((r) => setNews((r.data || []).slice(0, 3))).catch(() => {});
    endpoints.liveStreams().then((r) => setLive(r.data)).catch(() => {});
    endpoints.plans().then((r) => setPlans(r.data || [])).catch(() => {});
    endpoints.settings().then((r) => setCfg(r.data || {})).catch(() => {});
    endpoints.journalists().then((r) => setTeam(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="pb-28">
      <HomepageVideo />
      <div className="px-3 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <section className="pt-6 sm:pt-10" data-testid="home-hero">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E11D2A]/15 border border-[#E11D2A]/30 text-[#EF4444] text-[11px] font-bold uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] live-dot" /> {h.heroBadge || "Live now on 89.7 FM"}
          </div>
          <h1 className="mt-3 text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none text-white" data-testid="hero-slogan">
            {h.heroSlogan || "MURI SIPORO, NI IGITEGO!"}
          </h1>
          <p className="text-slate-300 mt-3 text-sm sm:text-base max-w-2xl">Live radio, live TV, football and shows from B&B KIGALI FM 89.7 — from Kigali to fans everywhere.</p>
          <div className="flex flex-wrap gap-2 sm:gap-3 mt-5">
            <button onClick={requestPlay} data-testid="hero-listen-btn"
              className="h-11 px-5 rounded-full bg-[#E11D2A] hover:bg-[#C8121F] text-white font-bold btn-glow flex items-center gap-2 transition-colors">
              <Radio className="w-5 h-5" /> {isPlaying ? "Playing live" : "Listen live"}
            </button>
            {isAuthed ? (
              <Link to="/pricing" data-testid="hero-plans-btn" className="h-11 px-5 rounded-full bg-white/5 border border-[#273042] text-white font-semibold flex items-center gap-2 hover:bg-white/10 transition-colors"><Crown className="w-5 h-5" /> Plans</Link>
            ) : (
              <button onClick={() => window.dispatchEvent(new Event("open-auth"))} data-testid="hero-create-account-btn"
                className="h-11 px-5 rounded-full bg-white/5 border border-[#273042] text-white font-semibold hover:bg-white/10 transition-colors">Join free</button>
            )}
          </div>
        </section>

        <div className="mt-6"><LiveStrip live={live} nowPlaying={nowPlaying} /></div>

        <section className="mt-6" data-testid="home-services">
          <h2 className="text-lg sm:text-xl font-bold text-white mb-3">Our services</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-4">
            {TILES.map((t, i) => <ServiceTile key={t.id} t={t} i={i} />)}
          </div>
        </section>

        <div className="mt-6"><PlansStrip plans={plans} isSubscribed={isSubscribed} /></div>
        {news.length > 0 && <NewsList news={news} />}
        <TeamStrip team={team} />
        <div className="mt-8"><AdSlot placement="home" position="bottom" /></div>
      </div>
    </div>
  );
}
